import _ from "lodash";
import moment from "moment";
import Kichiri from "./KichiriService";
import UserService from "./UserService";
import toast from "react-hot-toast";

const UtilityService = {
	/**
	 * Simple Timeout promise
	 *
	 * @param {Number} ms
	 * @return {Promise}
	 */
	timeout: function(ms) {
		return new Promise(function(resolve, reject) {
			setTimeout(resolve, ms);
		});
	},

	async submitTicket({ name, description }) {
		let { authToken } = UserService.getUserData();

		try {
			let response = await Kichiri.support.create({ name, description }, {}, authToken);

			console.log(response);

			return response.data;
		} catch (error) {
			console.log(error);
		}

		return null;
	},

	getOrdinal(n) {
		var s = ["th", "st", "nd", "rd"];
		var v = n % 100;
		return n + (s[(v - 20) % 10] || s[v] || s[0]);
	},

	/**
	 *
	 * @param {unknown} value
	 * @returns {boolean}
	 */
	isNullish: value => _.isNil(value),

	/**
	 *
	 * @param {unknown} value
	 * @returns {boolean}
	 */
	isNotNullish: value => !_.isNil(value),

	/**
	 * Get the relative dates for message dividers
	 *
	 * @param {Date} date
	 */
	getRelativeDate(date) {
		let momentDate = moment(date);

		if (momentDate.year() !== moment().year()) {
			return momentDate.format("ddd, MMM Do, YYYY");
		}

		return momentDate.calendar(null, {
			sameDay: "[Today]",
			nextDay: "[Tomorrow]",
			nextWeek: "dddd",
			lastDay: "[Yesterday]",
			lastWeek: "ddd, MMM Do",
			sameElse: "ddd, MMM Do"
		});
	},

	/*
	 * Returns a boolean whether or not container is empty
	 * @param {unknown} value
	 * @returns {boolean}
	 */
	isEmpty: value => _.isEmpty(value),

	/**
	 *
	 * @param {object} object
	 * @param {string} path
	 * @returns
	 */
	isPathFoundInObject: (object, path) => _.hasIn(object, path),

	/**
	 *
	 * @param {any} value
	 * @param {any} defaultValue
	 * @returns {any}
	 */
	defaultTo: ({ value, defaultValue }) => _.defaultTo(value, defaultValue),

	/**
	 * @description Safetly derives the property from object otherwise defaults to undefined, similar to ?. chaning operator in newer versions of JS
	 * @param {object} object
	 * @param {string} path
	 * @returns {any}
	 */
	getPropertyFromObject: ({ object, path }) => _.get(object, path),

	/**
	 * @description Create a deep copy of an object
	 * @param {Object} object
	 * @returns {Object}
	 */
	deepCopy: object => _.cloneDeep(object),

	/**
	 * @description Returns a boolean whether or not the text is in email format
	 * @param {object} param
	 * @param {string} param.text
	 * @returns {boolean} true if text is in email format
	 */
	isTextInEmailFormat: ({ text }) => {
		const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return emailPattern.test(text);
	},

	/**
	 * Validates the budget format to ensure it follows the "1h 30m" or "1h" or "30m" pattern.
	 *
	 * @param {string} budget - The budget string to validate.
	 * @returns {boolean} - Returns true if the format is valid, otherwise false.
	 */
	validateBudgetFormat: budget => {
		return budget.split(" ").every(part => {
			const num = parseInt(part);
			return (part.endsWith("h") && !isNaN(num)) || (part.endsWith("m") && !isNaN(num));
		});
	},

	/**
	 * @description Replacer function for notes/references
	 * @param {boolean} capitalizeFirstLetter - Flag to capitalize the first letter of the note type.
	 * @param {string} type - The type of note, either "reference" or "note".
	 * @returns {string} - The formatted note type, potentially capitalized.
	 */
	getNoteType: ({ capitalizeFirstLetter, type }) => {
		let typeToReturn = type === "reference" ? "reference" : "note";
		// Capitalize if 'capitalizeFirstLetter' flag is set to true
		return capitalizeFirstLetter ? typeToReturn.charAt(0).toUpperCase() + typeToReturn.slice(1) : typeToReturn;
	},

	/**
	 * Displays an alert with the specified type, message, and configuration.
	 *
	 * @param {Object} options - Configuration options for the alert.
	 * @param {string} options.type - The type of alert (e.g., "success", "error", "info", "warning").
	 * @param {string} options.text - The message to display in the alert.
	 * @param {string} [options.id] - A unique identifier for the alert (optional).
	 * @param {number} [options.timeout] - The time in milliseconds before the alert automatically dismisses (optional); passed to react-hot-toast as `duration`, and its per-type default applies when omitted.
	 *
	 * @returns {void}
	 */
	alert: ({ type, text, id, timeout }) => {
		try {
			if (type === "dismiss" && id) {
				return toast.dismiss(id);
			}

			if (type === "info") {
				// Custom 'info' toast styling and icon
				return toast(text, {
					icon: "ℹ️",
					duration: timeout,
					style: {
						background: "#fff", // white background
						color: "var(--primary-color)", // primary text color
						fontWeight: 700
					}
				});
			}

			// Handle success, error, or other types
			return toast[type](text, { duration: timeout });
		} catch (error) {
			console.error(error);
		}
	},

	/**
	 * Initiates a download of a file represented by a blob.
	 *
	 * This function creates a temporary anchor (`<a>`) element, sets its `href` attribute to an object URL representing the blob,
	 * and sets the `download` attribute to the provided filename. It then programmatically clicks the anchor to trigger the download.
	 * After the download is initiated, the function cleans up by removing the anchor from the document and revoking the object URL to release memory.
	 *
	 * @param {Object} params - The parameters object.
	 * @param {Blob} params.blob - The blob representing the file to be downloaded.
	 * @param {string} params.fileName - The name of the file to be downloaded.
	 */
	downloadFile({ blob, fileName }) {
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement("a");

		a.href = url;
		a.target = "_blank";
		a.download = fileName;
		document.body.appendChild(a);
		a.click();
		a.remove();

		// Clean up the URL object
		window.URL.revokeObjectURL(url);
	},

	/**
	 * A new list with the item taken out when the list has it, or added at the end when it does not.
	 * The given list is left as it was.
	 *
	 * @param {Object} params
	 * @param {Array} params.list
	 * @param {*} params.item
	 * @return {Array}
	 */
	toggleInList({ list, item }) {
		const hasItem = list.includes(item);
		const toggledList = hasItem ? list.filter(candidate => candidate !== item) : [...list, item];
		return toggledList;
	},

	/**
	 * Opens a blob in a new tab for the browser to render inline (a PDF or image opens as a page),
	 * instead of downloadFile's forced save-to-disk.
	 *
	 * @param {Object} params
	 * @param {Blob} params.blob
	 * @param {String} params.fileName - unused by the browser's inline viewer; kept for callers that fall back to a download when the browser can't render the type inline
	 */
	previewFile({ blob, fileName }) {
		const url = window.URL.createObjectURL(blob);
		const opened = window.open(url, "_blank");
		if (!opened) this.downloadFile({ blob, fileName });
	},

	/**
	 * Coerces any value to a trimmed string; null and undefined become "".
	 *
	 * @param {*} value
	 * @returns {string}
	 */
	toTrimmedText: value => {
		const text = String(value || "");
		const trimmedText = text.trim();
		return trimmedText;
	},

	/**
	 * Coerces any value to a trimmed, lower-cased string for case-insensitive
	 * comparison and search; null and undefined become "".
	 *
	 * @param {*} value
	 * @returns {string}
	 */
	toSearchText: value => {
		const trimmedText = UtilityService.toTrimmedText(value);
		const searchText = trimmedText.toLowerCase();
		return searchText;
	},

	/**
	 * Returns the account number at the very start of a string, e.g. "5010 - Food" becomes
	 * "5010" and "1000-1 Petty cash" keeps its sub-account suffix, "1000-1". A separator not
	 * followed by digits is not part of the number. Returns "" when the string does not begin
	 * with a digit.
	 *
	 * This is the one reading of an account number in the browser, and it matches the API's
	 * accountIdentityService.readAccountNumberFromLabel; the two must agree or the browser and
	 * the database recognise different accounts in the same label.
	 *
	 * @param {*} value
	 * @returns {string}
	 */
	getLeadingNumber: value => {
		const leadingNumber = String(value || "").trim().match(/^(\d+(?:[-.]\d+)*)/);
		return leadingNumber ? leadingNumber[1] : "";
	},

	/**
	 * Formats a number in accounting style: rounded to a whole number with thousands
	 * separators, negatives wrapped in parentheses, and zero (or a blank value) shown
	 * as an en dash.
	 *
	 * @param {*} value
	 * @returns {string}
	 */
	formatAccountingAmount: value => {
		const amount = Number(value) || 0;
		if (amount === 0) return "–";

		const roundedMagnitude = Math.abs(Math.round(amount));
		const formattedMagnitude = roundedMagnitude.toLocaleString("en-CA");
		return amount < 0 ? `(${formattedMagnitude})` : formattedMagnitude;
	},

	/**
	 * Builds a stable identity key for a File from its name and size.
	 *
	 * @param {File} file
	 * @returns {string}
	 */
	getFileKey: file => `${file.name}:${file.size}`,

	/**
	 * Returns the candidate files whose identity is not already present in the existing files.
	 *
	 * @param {Object} params
	 * @param {File[]} params.existingFiles
	 * @param {File[]} params.candidateFiles
	 * @returns {File[]}
	 */
	getFilesNotAlreadyAdded: ({ existingFiles, candidateFiles }) => {
		const existingKeys = new Set(existingFiles.map(file => UtilityService.getFileKey(file)));
		const newFiles = candidateFiles.filter(file => !existingKeys.has(UtilityService.getFileKey(file)));
		return newFiles;
	},

	/**
	 * Returns a copy of the object with `changes` merged into the record stored at `key`.
	 *
	 * @param {Object} params
	 * @param {Object} params.object
	 * @param {string} params.key
	 * @param {Object} params.changes
	 * @returns {Object}
	 */
	mergeIntoKey: ({ object, key, changes }) => ({
		...object,
		[key]: { ...(object[key] || {}), ...changes },
	}),

	/**
	 * Returns a copy of the object without the given key.
	 *
	 * @param {Object} params
	 * @param {Object} params.object
	 * @param {string} params.key
	 * @returns {Object}
	 */
	omitKey: ({ object, key }) => _.omit(object, [key]),

	/**
	 * Returns a copy of the object with every entry whose value equals oldValue
	 * replaced by newValue.
	 *
	 * @param {Object} params
	 * @param {Object} params.object
	 * @param {*} params.oldValue
	 * @param {*} params.newValue
	 * @returns {Object}
	 */
	replaceValueInMap: ({ object, oldValue, newValue }) => _.mapValues(object, value => (value === oldValue ? newValue : value)),

	/**
	 * Returns a copy of the object with every value replaced by toValue(value).
	 *
	 * @param {Object} params
	 * @param {Object} params.object
	 * @param {Function} params.toValue
	 * @returns {Object}
	 */
	mapValues: ({ object, toValue }) => _.mapValues(object, toValue),

	/**
	 * Splits one CSV line into trimmed cell values, honouring quoted fields and escaped quotes.
	 *
	 * @param {string} line
	 * @returns {string[]}
	 */
	parseCsvLine: line => {
		const cells = [];
		let currentCell = "";
		let insideQuotes = false;

		for (let position = 0; position < line.length; position++) {
			const character = line[position];
			const nextCharacter = line[position + 1];

			if (insideQuotes) {
				const isEscapedQuote = character === '"' && nextCharacter === '"';
				const isClosingQuote = character === '"' && nextCharacter !== '"';

				if (isEscapedQuote) {
					currentCell += '"';
					position++;
				} else if (isClosingQuote) {
					insideQuotes = false;
				} else {
					currentCell += character;
				}
				continue;
			}

			const isOpeningQuote = character === '"';
			const isCellSeparator = character === ",";

			if (isOpeningQuote) {
				insideQuotes = true;
			} else if (isCellSeparator) {
				cells.push(currentCell);
				currentCell = "";
			} else {
				currentCell += character;
			}
		}

		cells.push(currentCell);
		const trimmedCells = cells.map(cell => cell.trim());
		return trimmedCells;
	},

	/**
	 * Parses CSV text into a matrix of trimmed cells, dropping blank lines.
	 *
	 * @param {string} text
	 * @returns {string[][]}
	 */
	parseCsv: text => {
		const isBlankLine = line => line.trim() === "";
		const lines = text.split("\n");
		const dataLines = lines.filter(line => !isBlankLine(line));
		const cellMatrix = dataLines.map(line => UtilityService.parseCsvLine(line));
		return cellMatrix;
	},

	/**
	 * Parses a currency-style string into a number; parentheses denote a negative.
	 *
	 * @param {*} value
	 * @returns {number}
	 */
	parseAmount: value => {
		if (value === null || value === undefined) return 0;

		const text = String(value).trim();
		if (text === "") return 0;

		const currencyFormattingCharacters = "(),$ \t";
		const isNegative = text.startsWith("(") && text.endsWith(")");
		const withoutCurrencyFormatting = [...text].filter(character => !currencyFormattingCharacters.includes(character)).join("");
		const amount = parseFloat(withoutCurrencyFormatting);

		if (isNaN(amount)) return 0;
		const signedAmount = isNegative ? -amount : amount;
		return signedAmount;
	},

	/**
	 * Splits text into lower-cased word tokens bounded by non-alphanumeric characters, so
	 * callers can do whole-word matching by comparing token sequences rather than raw
	 * substrings (so "usa" tokenizes apart from "usage").
	 *
	 * @param {Object} params
	 * @param {String} params.text
	 * @returns {String[]} lower-cased word tokens
	 */
	toWordTokens: ({ text }) => {
		const loweredText = (text || "").toLowerCase();
		const isWordCharacter = character => (character >= "a" && character <= "z") || (character >= "0" && character <= "9");
		const wordCharactersOnly = [...loweredText].map(character => (isWordCharacter(character) ? character : " ")).join("");
		const words = wordCharactersOnly.split(" ").filter(Boolean);
		return words;
	},

	/**
	 * Returns the index of the header that best matches any of the keywords, or -1.
	 * Matches strongest first: a header equal to a keyword, then a keyword appearing as a
	 * whole word in the header, then a substring match for keywords of three or more letters.
	 * Short abbreviations like "cr", "dr" and "gl" therefore never match inside longer words
	 * (e.g. "cr" no longer picks up "des-cr-iption").
	 *
	 * @param {string[]} headers
	 * @param {string[]} keywords
	 * @returns {number}
	 */
	guessColumnIndex: (headers, keywords) => {
		const normalizedHeaders = headers.map(header => (header || "").toLowerCase().trim());
		const wordsPerHeader = headers.map(header => UtilityService.toWordTokens({ text: header }));

		const headerEqualsAnyKeyword = header => keywords.some(keyword => header === keyword);
		const exactMatchIndex = normalizedHeaders.findIndex(headerEqualsAnyKeyword);
		if (exactMatchIndex !== -1) return exactMatchIndex;

		const wordsIncludeAnyKeyword = words => keywords.some(keyword => words.includes(keyword));
		const wholeWordMatchIndex = wordsPerHeader.findIndex(wordsIncludeAnyKeyword);
		if (wholeWordMatchIndex !== -1) return wholeWordMatchIndex;

		const longKeywords = keywords.filter(keyword => keyword.length >= 3);
		const headerContainsAnyLongKeyword = header => longKeywords.some(keyword => header.includes(keyword));
		const substringMatchIndex = normalizedHeaders.findIndex(headerContainsAnyLongKeyword);
		return substringMatchIndex;
	},

	/**
	 * Escapes one value for a CSV cell: cells containing commas, quotes or line
	 * breaks are wrapped in quotes with inner quotes doubled.
	 *
	 * @param {*} value
	 * @returns {string}
	 */
	escapeCsvCell: value => {
		const text = value === null || value === undefined ? "" : String(value);
		const charactersRequiringQuotes = ',"\n\r';
		const needsQuotes = [...text].some(character => charactersRequiringQuotes.includes(character));
		if (!needsQuotes) return text;

		const withDoubledQuotes = text.split('"').join('""');
		const quotedCell = `"${withDoubledQuotes}"`;
		return quotedCell;
	},

	/**
	 * Builds CSV text from a header row and data rows, escaping every cell.
	 *
	 * @param {Object} params
	 * @param {string[]} params.headers
	 * @param {Array<Array<*>>} params.rows
	 * @returns {string}
	 */
	buildCsv: ({ headers, rows }) => {
		const allRows = [headers, ...rows];
		const csvLines = allRows.map(row => row.map(cell => UtilityService.escapeCsvCell(cell)).join(","));
		const csvText = csvLines.join("\n");
		return csvText;
	},

	isNotAllowedResource : ({response}) => {
		if (response &&  response.error && response.error.code === 403) {
			return true;
		}
		return false;
	},
	/**
	 * Converts any supported date-like input into a **date-only** string in `YYYY-MM-DD` format.
	 *
	 * Why this exists:
	 * - Normalizes dates across the app so APIs / filters / UI comparisons don’t get thrown off by time zones or time parts.
	 *
	 * What it accepts:
	 * - `null` / `undefined` / empty → returns `null`
	 * - A string already in `YYYY-MM-DD` → returns it unchanged (strict validation)
	 * - Anything Moment can parse (Date object, ISO string, etc.) → returns the date portion as `YYYY-MM-DD`
	 *
	 * Safety behavior:
	 * - If the value cannot be parsed into a valid date, returns `null` (instead of throwing).
	 *
	 * @param {any} params.value - Date-like value (string/Date/etc.)
	 * @returns {string|null} Date-only string in `YYYY-MM-DD`, or null if invalid.
	 */
	toDateOnly: ({value}) => {
		if (!value) return null;

		// If it's already "YYYY-MM-DD", keep it exactly (strict parse)
		if (typeof value === "string") {
			const strict = moment(value, "YYYY-MM-DD", true);
			if (strict.isValid()) return strict.format("YYYY-MM-DD");
		}

		// Otherwise, try normal moment parsing (handles Date objects, other strings, etc.)
		const parsed = moment(value);
		if (!parsed.isValid()) return null;

		return parsed.format("YYYY-MM-DD");
	},

	/**
	 * How long something took, in words short enough for a column: "0.3 s", "4.1 s", "1 min 6 s".
	 * A time not measured gives null.
	 *
	 * @param {Object} params
	 * @param {Number|null} params.milliseconds
	 * @returns {string|null}
	 */
	describeDuration: ({ milliseconds }) => {
		if (milliseconds === null || milliseconds === undefined || !Number.isFinite(Number(milliseconds))) return null;
		const seconds = Number(milliseconds) / 1000;
		if (seconds < 60) return `${seconds.toFixed(1)} s`;
		const wholeSeconds = Math.round(seconds);
		return `${Math.floor(wholeSeconds / 60)} min ${wholeSeconds % 60} s`;
	},

	/**
	 * Formats a date value with a moment format, returning a fallback when empty/invalid.
	 *
	 * @param {Object} params
	 * @param {*} params.value - Date, ISO string, or anything moment can parse
	 * @param {string} [params.format="MMM D, YYYY"]
	 * @param {string} [params.fallback="Unknown date"]
	 * @returns {string}
	 */
	formatDate: ({ value, format = "MMM D, YYYY", fallback = "Unknown date" }) => {
		if (!value) return fallback;

		const parsed = moment(value);
		return parsed.isValid() ? parsed.format(format) : fallback;
	},

	/**
	 * A moment in time as the app writes one everywhere — "Oct 3, 2026 9:13 AM" — so no screen spells
	 * the format itself.
	 *
	 * @param {Object} params
	 * @param {String|Date} params.value
	 * @param {String} [params.fallback]
	 * @return {String}
	 */
	formatDateTime: ({ value, fallback = "Unknown date" }) => {
		const formatted = UtilityService.formatDate({ value, format: "MMM D, YYYY h:mm A", fallback });
		return formatted;
	},

	/**
	 * Describes a span of dates the way a person reads a shelf: a heading naming the month, and
	 * a detail line with the days. A span of up to about six weeks is one month's worth and is
	 * named by the month most of its days fall in, so June 30 to July 31 reads as "Jul 2025". A
	 * longer span is named by its first and last months, "Jul 2025 – Jun 2026". The detail
	 * repeats the year only when the two ends fall in different years.
	 *
	 * @param {Object} params
	 * @param {*} params.start - Date, ISO string, or anything moment can parse
	 * @param {*} params.end
	 * @param {string} [params.fallback="Unknown period"] - the heading when either end is missing
	 * @returns {Object} { heading, detail }
	 */
	/**
	 * Writes two dates as one range, "Jun 30, 2025 – Jun 30, 2026", with a question mark standing
	 * in for an end that is missing, so a range with one known end still reads as a range.
	 *
	 * @param {Object} params
	 * @param {*} params.start - Date, ISO string, or anything moment can parse
	 * @param {*} params.end
	 * @returns {string}
	 */
	describeDateRange: ({ start, end }) => {
		const from = UtilityService.formatDate({ value: start, format: "MMM D, YYYY", fallback: "?" });
		const to = UtilityService.formatDate({ value: end, format: "MMM D, YYYY", fallback: "?" });
		const range = `${from} – ${to}`;
		return range;
	},

	/**
	 * A count with its noun agreeing: "1 statement", "12 statements". Every screen that says how
	 * many of something was doing this with its own ternary; one place means one spelling.
	 *
	 * @param {Object} params
	 * @param {Number} params.count
	 * @param {string} params.singular
	 * @param {string} [params.plural] - defaults to the singular with an s
	 * @returns {string}
	 */
	describeCount: ({ count, singular, plural = null }) => {
		const noun = count === 1 ? singular : plural || singular + "s";
		const described = `${count} ${noun}`;
		return described;
	},

	describeDateSpan: ({ start, end, fallback = "Unknown period" }) => {
		if (!start || !end) return { heading: fallback, detail: "" };

		const first = moment(start);
		const last = moment(end);
		if (!first.isValid() || !last.isValid()) return { heading: fallback, detail: "" };

		const daysCovered = last.diff(first, "days");
		const runsAcrossMonths = daysCovered > 45;
		const middle = first.clone().add(Math.floor(daysCovered / 2), "days");
		const heading = runsAcrossMonths ? `${first.format("MMM YYYY")} – ${last.format("MMM YYYY")}` : middle.format("MMM YYYY");

		const endsInTheSameYear = first.year() === last.year();
		const detail = endsInTheSameYear ? `${first.format("MMM D")} – ${last.format("MMM D")}` : `${first.format("MMM D, YYYY")} – ${last.format("MMM D, YYYY")}`;

		return { heading, detail };
	},

	/**
	 * Returns the percentage change from one number to another, rounded to one
	 * decimal, or null when the starting value is zero (no baseline to change from).
	 * A positive result means `to` is larger than `from`.
	 *
	 * @param {Object} params
	 * @param {Number} params.from - The baseline value
	 * @param {Number} params.to - The new value
	 * @returns {Number|null}
	 */
	calculatePercentChange: ({ from, to }) => {
		const baseline = Number(from) || 0;
		if (baseline === 0) return null;

		const change = ((Number(to) - baseline) / Math.abs(baseline)) * 100;
		const roundedChange = Math.round(change * 10) / 10;
		return roundedChange;
	},

	/**
	 * An amount written the way money is written, with the sign in front of the dollar rather than
	 * in front of the digits. formatCurrency gives the digits and no symbol, so every caller wanting
	 * a readable figure was adding the same two lines and disagreeing about where the minus goes.
	 *
	 * @param {Object} params
	 * @param {Number} params.value
	 * @returns {String}
	 */
	formatSignedCurrency: ({ value }) => {
		const amount = Number(value || 0);
		const sign = amount < 0 ? "-$" : "$";
		return sign + UtilityService.formatCurrency(Math.abs(amount));
	},

	/**
	 * Formats a number as a Canadian-dollar amount string with two decimals (no
	 * currency symbol); null, undefined and non-numbers become "0.00".
	 *
	 * @param {*} value
	 * @returns {string}
	 */
	formatCurrency: value => {
		const amount = Number(value || 0);
		const safeAmount = isNaN(amount) ? 0 : amount;
		const formattedAmount = safeAmount.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
		return formattedAmount;
	},

	/**
	 * The words a fixed list gives a code (a reason category, a stage, a verdict). A code the list
	 * does not have is shown as the code itself, so a gap in the list is visible on screen rather
	 * than blank.
	 *
	 * @param {Object} params
	 * @param {Object} params.labels code → words
	 * @param {string} params.key
	 * @returns {string}
	 */
	labelFor({ labels, key }) {
		const label = Object.prototype.hasOwnProperty.call(labels, key) ? labels[key] : key;
		return label;
	},

	/**
	 * Formats an amount kept in whole cents as a Canadian-dollar amount string with two decimals
	 * (no currency symbol), through formatCurrency.
	 *
	 * @param {Object} params
	 * @param {number} params.cents
	 * @returns {string}
	 */
	formatCentsAsCurrency({ cents }) {
		const dollars = Number(cents) / 100;
		const formattedAmount = UtilityService.formatCurrency(dollars);
		return formattedAmount;
	},

	/**
	 * Formats an amount for display as a dollar figure — "$1,234.56", or "-$1,234.56" when negative so
	 * the sign reads before the symbol rather than inside it. Absent values render as `emptyText`
	 * rather than as zero, because "nothing was reported" and "reported as nil" are different facts.
	 * Delegates the number formatting to formatCurrency so there is one implementation of it.
	 *
	 * @param {Object} params
	 * @param {*} params.value
	 * @param {string} [params.emptyText]
	 * @returns {string}
	 */
	formatMoney: ({ value, emptyText = "—" }) => {
		const isAbsent = value === null || value === undefined || value === "";
		if (isAbsent) {
			return emptyText;
		}

		const amount = Number(value);
		if (isNaN(amount)) {
			return emptyText;
		}

		const isNegative = amount < 0;
		const formattedMagnitude = UtilityService.formatCurrency(Math.abs(amount));
		const sign = isNegative ? "-" : "";
		return `${sign}$${formattedMagnitude}`;
	},

	/**
	 * Copies text to the clipboard. Returns true on success, false otherwise.
	 *
	 * @param {Object} params
	 * @param {string} params.text
	 * @returns {Promise<boolean>}
	 */
	copyToClipboard: async ({ text }) => {
		if (!text) return false;

		try {
			await navigator.clipboard.writeText(text);
			return true;
		} catch (error) {
			console.log(error);
			return false;
		}
	},

	/**
	 * The message the server gave for a refused request, without the "Error: " prefix it is
	 * stringified with, or the plain fallback when the server gave none.
	 *
	 * @param {Object} params
	 * @param {Object} params.error - The error a request threw
	 * @param {String} params.fallback - What to say when the server said nothing
	 * @returns {String}
	 */
	describeRequestError: ({ error, fallback }) => {
		const hasServerMessage = !!(error && error.response && error.response.data && error.response.data.error);
		if (!hasServerMessage) return fallback;
		const serverText = String(error.response.data.error);
		const serverMessage = serverText.startsWith("Error: ") ? serverText.slice("Error: ".length) : serverText;
		return serverMessage || fallback;
	},

	/**
	 * Creates reusable table column resizer that persist widths.
	 *
	 * Creates resize handlers on table columns, and allows resizing between minimum and maximum provided width
	 * @param {Object} params
	 * @param {Function} params.getDefaultColumnWidths
	 * @param {Function} params.getColumnStorageKey
	 * @param {number} [params.minWidth=80]
	 * @param {number} [params.maxWidth=900]
	 * @param {string} [params.cellSelector=".t__row__item"]
	 * @param {string} [params.truncatedClassName="t__row__item--truncated"]
	 * @param {string} [params.scrollableContainerSelector=".scrollable-container"]
	 * @param {string} [params.topScrollbarContentSelector=".scrollable-container__top__content"]
	 */
	createResizableTableColumns: ({
		getDefaultColumnWidths,
		getColumnStorageKey,
		onColumnWidthsChange,
		minWidth = 80,
		maxWidth = 900,
		cellSelector = ".t__row__item",
		truncatedClassName = "t__row__item--truncated",
		scrollableContainerSelector = ".scrollable-container",
		topScrollbarContentSelector = ".scrollable-container__top__content",
	}) => {
		let tableElement = null;
		let activeResize = null;
		let resizeRaf = null;
		let lastWidths = null;
		let didResize = false;
		let suppressClickUntil = 0;
		const resizeDragThreshold = 3;

		// for suppressing accidental clicking on the columns header filters
		const suppressResizeClick = (event) => {
			if (Date.now() < suppressClickUntil) {
				event.preventDefault();
				event.stopPropagation();
				if (typeof event.stopImmediatePropagation === "function") {
					event.stopImmediatePropagation();
				}
			}
		};

		const getSavedColumnWidths = () => {
			try {
				const key = getColumnStorageKey();
				if (!key) {
					return null;
				}

				return JSON.parse(localStorage.getItem(key) || "null");
			} catch (error) {
				return null;
			}
		};

		const saveColumnWidths = (widths) => {
			try {
				const key = getColumnStorageKey();
				if (!key) {
					return;
				}

				localStorage.setItem(key, JSON.stringify(widths));
			} catch (error) {
				// Ignore 
			}
		};

		const getColStyle = (colId) => ({
			width: `var(--col-${colId}-w)`, //produces: width: var(--col-name-w)
		});

		const applyColumnWidths = (widths) => {
			if (!tableElement) {
				return;
			}
			
			const finalWidths = { ...getDefaultColumnWidths(), ...(widths || {}) };

			lastWidths = finalWidths; 

			//produce: (--col-[colname]-w: [width]px) for each column
			for (const [colId, width] of Object.entries(finalWidths)) {
				tableElement.style.setProperty(`--col-${colId}-w`, `${width}px`);
			}
		};

		const setColumnWidths = (widths) => { 
			applyColumnWidths(widths);
			saveColumnWidths(widths || {});
			requestAnimationFrame(() => {
				checkTruncation();
				syncTopScrollbar();
			});
		};

		const checkTruncation = () => {
			if (!tableElement) {
				return;
			}

			const items = tableElement.querySelectorAll(cellSelector);
			const len = items.length;
			const truncated = new Array(len);

			for (let i = 0; i < len; i++) {
				truncated[i] = items[i].scrollWidth > items[i].clientWidth;
			}

			for (let i = 0; i < len; i++) {
				const item = items[i];
				const shouldHaveClass = truncated[i];
				const hasClass = item.classList.contains(truncatedClassName);

				if (hasClass !== shouldHaveClass) {
					item.classList.toggle(truncatedClassName, shouldHaveClass);
				}
			}
		};

		// explicitly sync the scrollbar above the engagements table
		const syncTopScrollbar = () => {
			if (!tableElement) {
				return;
			}

			const topContent = tableElement
				.closest(scrollableContainerSelector)
				?.querySelector(topScrollbarContentSelector);

			if (topContent) {
				topContent.style.width = `${tableElement.scrollWidth}px`;
			}
		};

		const refreshLayout = () => {
			requestAnimationFrame(() => {
				checkTruncation();
				syncTopScrollbar();
			});
		};

		const stopResizeInteraction = () => {
			window.removeEventListener("pointermove", onResizeMove);
			window.removeEventListener("pointerup", onResizeEnd);
			window.removeEventListener("pointercancel", onResizeEnd);

			document.body.classList.remove("col-resizing");

			if (resizeRaf) {
				cancelAnimationFrame(resizeRaf);
				resizeRaf = null;
			}
		};

		const onResizeStart = (event) => {
			event.preventDefault();
			event.stopPropagation();

			const { colId } = event.currentTarget.dataset;
			if (!tableElement || !colId) {
				return;
			}

			const varName = `--col-${colId}-w`;
			const current = getComputedStyle(tableElement).getPropertyValue(varName).trim();
			const startWidth = Number(current.replace("px", "")) || event.currentTarget.parentElement.offsetWidth;

			activeResize = {
				colId,
				varName,
				startX: event.clientX,
				startWidth,
			};
			didResize = false;

			document.body.classList.add("col-resizing");

			if (event.currentTarget.setPointerCapture && typeof event.pointerId === "number") {
				event.currentTarget.setPointerCapture(event.pointerId);
			}

			window.addEventListener("pointermove", onResizeMove);
			window.addEventListener("pointerup", onResizeEnd);
			window.addEventListener("pointercancel", onResizeEnd);
		};
		
		const onResizeMove = (event) => {
			if (!activeResize || !tableElement) {
				return;
			}

			const { varName, startX, startWidth } = activeResize;
			const delta = event.clientX - startX;
			const nextWidth = _.clamp(startWidth + delta, minWidth, maxWidth);

			if (Math.abs(delta) > resizeDragThreshold) {
				didResize = true;
			}

			if (resizeRaf) {
				return;
			}

			// update the column widths and scrollbar once per animation frame to avoid slow resizing
			resizeRaf = requestAnimationFrame(() => {
				
				//set css variable like: varName = "--col-name-w"
				tableElement.style.setProperty(varName, `${nextWidth}px`); 
				syncTopScrollbar();
				resizeRaf = null;

			});
		};

		const onResizeEnd = () => {
			const resize = activeResize;
			if (!resize) {
				return;
			}

			if (resizeRaf) {
				cancelAnimationFrame(resizeRaf);
				resizeRaf = null;
			}

			stopResizeInteraction();

			const { colId, varName } = resize;
			const finalWidth = parseFloat(getComputedStyle(tableElement).getPropertyValue(varName)) || 0;

			const saved = getSavedColumnWidths() || {};
			const nextWidths = { ...saved, [colId]: finalWidth };
			
			lastWidths = { ...(lastWidths || {}), [colId]: finalWidth };
			saveColumnWidths(nextWidths);

			onColumnWidthsChange(nextWidths);
			if (didResize) {
				suppressClickUntil = Date.now() + 250;
			}

			activeResize = null;
			didResize = false;
			refreshLayout();
		};

		const setTableElement = (element) => {
			if (!element) {
				cleanup();
				return;
			}

			document.addEventListener("click", suppressResizeClick, true);
			tableElement = element;
			applyColumnWidths(getSavedColumnWidths());
			refreshLayout();
		};

		const cleanup = () => {
			stopResizeInteraction();
			activeResize = null;
			didResize = false;
			suppressClickUntil = 0;
			tableElement = null;
			document.removeEventListener("click", suppressResizeClick, true);
		};

		return {
			setTableElement,
			getColStyle,
			checkTruncation,
			syncTopScrollbar,
			refreshLayout,
			onResizeStart,
			onResizeMove,
			onResizeEnd,
			cleanup,
			setColumnWidths,
		};
	},

	/**
	 * Parses a "YYYY-MM-DD" string into a local-timezone Date; empty input returns null.
	 *
	 * @param {string} isoDate
	 * @returns {Date|null}
	 */
	parseLocalDate: isoDate => {
		if (!isoDate) return null;
		const [year, month, day] = isoDate.split("-").map(Number);
		const localDate = new Date(year, month - 1, day);
		return localDate;
	},

	/**
	 * Shifts a "YYYY-MM-DD" date forward by a number of months and returns it in the
	 * same "YYYY-MM-DD" format; empty input returns null.
	 *
	 * @param {Object} params
	 * @param {string} params.isoDate
	 * @param {number} params.monthsToAdd
	 * @returns {string|null}
	 */
	addMonthsToIsoDate: ({ isoDate, monthsToAdd }) => {
		const shiftedDate = UtilityService.parseLocalDate(isoDate);
		if (!shiftedDate) return null;

		shiftedDate.setMonth(shiftedDate.getMonth() + monthsToAdd);
		const year = shiftedDate.getFullYear();
		const month = String(shiftedDate.getMonth() + 1).padStart(2, "0");
		const day = String(shiftedDate.getDate()).padStart(2, "0");
		const shiftedIsoDate = `${year}-${month}-${day}`;
		return shiftedIsoDate;
	},

	/**
	 * Formats a Date as a short readable Canadian date, e.g. "Aug 31, 2025".
	 *
	 * @param {Date} date
	 * @returns {string}
	 */
	formatShortDate: date => date.toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" }),

	/**
	 * Formats a number as a Canadian-dollar balance in accounting style: a leading
	 * dollar sign, thousands separators, and negatives wrapped in parentheses.
	 *
	 * @param {Object} params
	 * @param {number} params.balance
	 * @param {number} params.decimalPlaces
	 * @returns {string}
	 */
	formatDollarBalance: ({ balance, decimalPlaces }) => {
		const absoluteBalance = Math.abs(balance);
		const formattedMagnitude = absoluteBalance.toLocaleString("en-CA", { minimumFractionDigits: decimalPlaces, maximumFractionDigits: decimalPlaces });
		const isNegative = balance < 0;
		const formattedBalance = isNegative ? `($${formattedMagnitude})` : `$${formattedMagnitude}`;
		return formattedBalance;
	},

	/**
	 * Returns a file name without its final extension; names without a dot are unchanged.
	 *
	 * @param {string} fileName
	 * @returns {string}
	 */
	getFileNameWithoutExtension: fileName => {
		const lastDotIndex = fileName.lastIndexOf(".");
		if (lastDotIndex <= 0) return fileName;
		const nameWithoutExtension = fileName.slice(0, lastDotIndex);
		return nameWithoutExtension;
	},

	/**
	 * Returns true when a file name ends with the given extension, ignoring case.
	 *
	 * @param {Object} params
	 * @param {string} params.fileName
	 * @param {string} params.suffix - The extension to test for, e.g. ".xlsx"
	 * @returns {boolean}
	 */
	fileNameEndsWith: ({ fileName, suffix }) => {
		const lowerCaseFileName = String(fileName || "").toLowerCase();
		const lowerCaseSuffix = String(suffix || "").toLowerCase();
		const endsWithSuffix = lowerCaseFileName.endsWith(lowerCaseSuffix);
		return endsWithSuffix;
	},

	/**
	 * Returns true when the file name ends with a .pdf extension (case-insensitive).
	 *
	 * @param {string} fileName
	 * @returns {boolean}
	 */
	isPdfFile: fileName => {
		const isPdf = (fileName || "").toLowerCase().endsWith(".pdf");
		return isPdf;
	},

	/**
	 * States a file size the way a person reads one. A size in bytes is what the
	 * browser reports and what nobody wants to see next to a file name.
	 *
	 * @param {Object} params
	 * @param {Number} params.bytes
	 * @returns {String} e.g. "1.4 MB", or "" when the size is unknown
	 */
	formatFileSize: ({ bytes }) => {
		const size = Number(bytes);
		if (!size || isNaN(size) || size < 0) return "";

		const units = ["bytes", "KB", "MB", "GB"];
		let scaledSize = size;
		let unitIndex = 0;
		while (scaledSize >= 1024 && unitIndex < units.length - 1) {
			scaledSize = scaledSize / 1024;
			unitIndex++;
		}

		const decimals = unitIndex === 0 || scaledSize >= 100 ? 0 : 1;
		return `${scaledSize.toFixed(decimals)} ${units[unitIndex]}`;
	},

	/**
	 * Reads a value saved in local storage, giving back the fallback whenever there is
	 * nothing there or what is there cannot be read. Storage survives releases and can
	 * hold something written by an older version of the app, so a parse failure has to
	 * be an empty result rather than an exception thrown at whoever asked.
	 *
	 * @param {Object} params
	 * @param {String} params.key
	 * @param {*} params.fallback - returned when nothing readable is stored
	 * @returns {*}
	 */
	readStoredJson: ({ key, fallback = null }) => {
		try {
			const stored = localStorage.getItem(key);
			if (stored === null || stored === undefined || stored === "") return fallback;
			return JSON.parse(stored);
		} catch (error) {
			return fallback;
		}
	},

	/**
	 * Remembers a small per-viewer convenience in the browser, such as which sections of a list
	 * were folded shut. A browser that refuses storage is not an error worth showing; the value
	 * simply is not remembered.
	 *
	 * @param {Object} params
	 * @param {string} params.key
	 * @param {*} params.value - anything JSON can hold
	 * @returns {boolean} whether it was stored
	 */
	writeStoredJson: ({ key, value }) => {
		try {
			const serialized = JSON.stringify(value);
			localStorage.setItem(key, serialized);
			return true;
		} catch (error) {
			return false;
		}
	},

	/**
	 * Runs an async job over a list several at a time rather than one after another,
	 * never exceeding the limit. Used where each item costs a slow network call and
	 * doing them strictly in turn would leave the user waiting minutes. Every item is
	 * still attempted; the jobs themselves decide what to do with a failure.
	 *
	 * @param {Object} params
	 * @param {Array} params.items
	 * @param {number} params.limit - how many may be in flight at once
	 * @param {Function} params.run - async (item, index) => void
	 * @returns {Promise<void>}
	 */
	runWithConcurrencyLimit: async ({ items, limit, run }) => {
		const queue = [...(items || [])];
		let nextIndex = 0;

		const worker = async () => {
			while (true) {
				const index = nextIndex;
				nextIndex += 1;
				if (index >= queue.length) return;
				await run(queue[index], index);
			}
		};

		const workerCount = Math.max(1, Math.min(limit || 1, queue.length));
		const workers = Array.from({ length: workerCount }, worker);
		await Promise.all(workers);
	},

	/**
	 * Builds a stable 64-character hex identity for a piece of text. The same text
	 * always produces the same value, so it can identify content across sessions
	 * and machines. It is an identity, not a security digest — never use it where a
	 * cryptographic hash is required.
	 *
	 * @param {Object} params
	 * @param {string} params.text
	 * @returns {string} 64 hex characters
	 */
	buildStableHash: ({ text }) => {
		const input = String(text || "");
		const runningValues = [0x811c9dc5, 0x01000193, 0x9e3779b9, 0x85ebca6b, 0xc2b2ae35, 0x27d4eb2f, 0x165667b1, 0xd3a2646c];

		for (let position = 0; position < input.length; position++) {
			const character = input.charCodeAt(position);
			for (let slot = 0; slot < runningValues.length; slot++) {
				let value = runningValues[slot] ^ (character + position + slot);
				value = Math.imul(value, 0x01000193);
				runningValues[slot] = value >>> 0;
			}
		}

		const hexParts = runningValues.map(value => (value >>> 0).toString(16).padStart(8, "0"));
		const stableHash = hexParts.join("");
		return stableHash;
	},
};

export default UtilityService;
